import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    message: '',
    services: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [showPopup, setShowPopup] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleServiceToggle = (service) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const apiUrl = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:5000/api/contact' 
        : '/api/contact';
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        setSubmitStatus({ type: 'success', message: '' });

        // Show animated popup
        setShowPopup(true);
        setTimeout(() => {
          setShowPopup(false);
        }, 3000);

        // Reset form
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          message: '',
          services: []
        });
      } else {
        setSubmitStatus({
          type: 'error',
          message: data.message || 'Something went wrong. Please try again.'
        });
      }
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Unable to send message. Please try again or contact us directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    'Laser Cutting',
    'CNC Bending',
    'Welding',
    'Surface Coating',
    'Metal Fabrication',
    'Other'
  ];

  const socialLinks = [
    { icon: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z', name: 'Facebook', url: 'https://www.facebook.com/profile.php?id=61587464441215', color: '#1877F2' },
    { icon: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z', name: 'Instagram', url: 'https://www.instagram.com/alphalasertechnologies/', color: '#E4405F' },
    { icon: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z', name: 'LinkedIn', url: 'https://www.linkedin.com/company/alpha-laser-technologies', color: '#0A66C2' },
    { icon: 'M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z', name: 'YouTube', url: 'https://www.youtube.com/@alphalasertechnologies', color: '#FF0000' },
  ];

  return (
    <section id="contact" className="relative min-h-screen overflow-hidden">
      {/* Fixed Parallax Background */}
      <div 
        className="fixed inset-0 bg-cover bg-center bg-no-repeat z-0 opacity-30"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1587293852726-70cdb56c2866?q=80&w=2072&auto=format&fit=crop')",
        }}
      >
        {/* Dark overlay for better readability */}
        <div className="absolute inset-0 bg-black/50" />
        
        {/* Geometric Shape Overlays */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Floating Blob 1 */}
          <div 
            className="absolute top-10 right-10 w-[500px] h-[500px] animate-pulse"
            style={{
              background: 'linear-gradient(135deg, rgba(15,14,151,0.15) 0%, rgba(15,14,151,0.1) 100%)',
              clipPath: 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)',
              animation: 'float 20s ease-in-out infinite',
            }}
          />
          
          {/* Floating Blob 2 */}
          <div 
            className="absolute bottom-20 left-10 w-[400px] h-[400px]"
            style={{
              background: 'linear-gradient(225deg, rgba(15,14,151,0.2) 0%, rgba(227,41,52,0.15) 100%)',
              clipPath: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)',
              animation: 'float 25s ease-in-out infinite reverse',
            }}
          />
          
          {/* Star Shape */}
          <div 
            className="absolute top-1/2 left-1/3 w-48 h-48 opacity-30"
            style={{
              background: 'radial-gradient(circle, rgb(15,14,151) 0%, transparent 70%)',
              clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
            }}
          />
          
          {/* Wavy Lines SVG */}
          <svg className="absolute inset-0 w-full h-full opacity-5" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="waves" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                <path d="M0 50 Q 25 25, 50 50 T 100 50" stroke="rgb(15,14,151)" fill="none" strokeWidth="2"/>
                <path d="M0 70 Q 25 45, 50 70 T 100 70" stroke="rgb(15,14,151)" fill="none" strokeWidth="2"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#waves)" />
          </svg>
          
          {/* Cross Shape */}
          <div 
            className="absolute top-20 left-1/4 w-32 h-32 opacity-20"
            style={{
              background: 'linear-gradient(45deg, rgb(15,14,151) 0%, rgb(15,14,151) 100%)',
              clipPath: 'polygon(40% 0%, 60% 0%, 60% 40%, 100% 40%, 100% 60%, 60% 60%, 60% 100%, 40% 100%, 40% 60%, 0% 60%, 0% 40%, 40% 40%)',
            }}
          />
          
          {/* Diamond Chain */}
          <div 
            className="absolute bottom-40 right-1/4 w-40 h-40 opacity-25"
            style={{
              background: 'linear-gradient(135deg, rgba(15,14,151,0.3) 0%, rgba(15,14,151,0.3) 100%)',
              clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
            }}
          />
        </div>
      </div>

      {/* Content Container */}
      <div className="relative z-10 p-4 lg:p-8 pt-16 lg:pt-24">
        {/* Modern Split Layout */}
        <div className="flex flex-col lg:flex-row min-h-screen overflow-hidden rounded-3xl shadow-2xl max-w-7xl mx-auto">
        
        {/* Left Side - Contact Information */}
        <motion.div 
          className="lg:w-2/5 bg-white p-8 lg:p-12 flex flex-col justify-center rounded-3xl lg:rounded-none lg:rounded-l-3xl"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="space-y-12">
            {/* Connect us */}
            <div>
              <h1 className="text-5xl lg:text-6xl font-bold text-steel font-poppins tracking-tight">Connect with Alpha Laser Technologies</h1>
            </div>

            {/* Contact Info Sections */}
            <div className="space-y-10">
            {/* Chat to us */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-lg text-steel mb-2">Chat to us</h3>
                  <p className="text-gray-600 text-sm mb-2">Our friendly team is here to help.</p>
                  <a href="mailto:alphalaserinfo@gmail.com" className="text-primary hover:text-secondary transition-colors font-medium">
                    alphalaserinfo@gmail.com
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Visit us */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-lg text-steel mb-2">Visit us</h3>
                  <p className="text-gray-600 text-sm mb-2">Come say hello at our office HQ.</p>
                  <a 
                    href="https://www.google.com/maps/place/ALPHA+LASER+TECHNOLOGIES/@11.046290145895197,76.97662787170464,17z" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-steel not-italic hover:text-primary transition-colors block"
                  >
                    184/2, Infront of the IEC Bus Stop,<br />
                    Barathiyar Road, ManiyakaramPalayam,<br />
                    Ganapathy, Coimbatore-641006
                  </a>
                  <p className="text-steel font-semibold text-sm mt-2">GSTIN: 33ESZPR8850J1ZM</p>
                </div>
              </div>
            </motion.div>

            {/* Call us */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-bold text-lg text-steel mb-2">Call us</h3>
                  <p className="text-gray-600 text-sm mb-2">Mon-Sat from 9am to 6pm.</p>
                  <div className="space-y-1">
                    <a href="tel:+919363638811" className="block text-primary hover:text-secondary transition-colors font-medium">
                      +91 93 63 63 88 11
                    </a>
                    <a href="tel:+919363638822" className="block text-primary hover:text-secondary transition-colors font-medium">
                      +91 93 63 63 88 22
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

            {/* Social Links */}
            <motion.div
              className="pt-8 border-t border-gray-200"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
          >
            <div className="flex gap-4">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-14 h-14 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-all duration-300 group"
                  whileHover={{ scale: 1.1, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                  aria-label={social.name}
                >
                  <svg className="w-7 h-7 transition-colors" style={{ color: social.color }} fill="currentColor" viewBox="0 0 24 24">
                    <path d={social.icon} />
                  </svg>
                </motion.a>
              ))}
            </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Right Side - Form Section */}
        <motion.div 
          className="lg:w-3/5 bg-gradient-to-br from-[rgb(15,14,151)] via-[rgb(15,14,151)] to-[rgb(15,14,151)] p-8 lg:p-12 flex items-center justify-center rounded-3xl lg:rounded-none lg:rounded-r-3xl"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="w-full max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mb-8"
            >
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
                Alpha Laser Technologies - We Build For You 
              </h2>
              <p className="text-white/90 text-lg">
                Share your laser cutting & fabrication requirements with Alpha Laser Technologies Coimbatore.
              </p>
            </motion.div>

            <motion.form
              onSubmit={handleSubmit}
              className="space-y-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              {/* Name Input */}
              <div>
                <input
                  type="text"
                  name="fullName"
                  placeholder="Your name"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  className="w-full px-0 py-3 bg-transparent border-b-2 border-white/30 focus:border-white outline-none text-white placeholder-white/60 transition-all duration-300 text-lg"
                />
              </div>

              {/* Email Input */}
              <div>
                <input
                  type="email"
                  name="email"
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-0 py-3 bg-transparent border-b-2 border-white/30 focus:border-white outline-none text-white placeholder-white/60 transition-all duration-300 text-lg"
                />
              </div>

              {/* Message Input */}
              <div>
                <textarea
                  name="message"
                  placeholder="Tell us a little about the project..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="3"
                  className="w-full px-0 py-3 bg-transparent border-b-2 border-white/30 focus:border-white outline-none text-white placeholder-white/60 transition-all duration-300 resize-none text-lg"
                />
              </div>

              {/* Services Checkboxes */}
              <div className="pt-4">
                <p className="text-white font-semibold mb-4 text-lg">How can we help?</p>
                <div className="grid grid-cols-2 gap-4">
                  {services.map((service, index) => (
                    <motion.label
                      key={index}
                      className="flex items-center gap-3 cursor-pointer group"
                      whileHover={{ x: 5 }}
                    >
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={formData.services.includes(service)}
                          onChange={() => handleServiceToggle(service)}
                          className="sr-only"
                        />
                        <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all duration-300 ${
                          formData.services.includes(service)
                            ? 'bg-white border-white'
                            : 'bg-transparent border-white/40 group-hover:border-white'
                        }`}>
                          {formData.services.includes(service) && (
                            <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                      <span className="text-white font-medium">{service}</span>
                    </motion.label>
                  ))}
                </div>
              </div>

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={isSubmitting}
                className={`w-full font-bold py-4 px-8 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl mt-8 ${
                  isSubmitting 
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                    : 'bg-white hover:bg-gray-100 text-primary'
                }`}
                whileHover={!isSubmitting ? { scale: 1.02, y: -2 } : {}}
                whileTap={!isSubmitting ? { scale: 0.98 } : {}}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Sending...</span>
                  </div>
                ) : (
                  "Let's get started!"
                )}
              </motion.button>

              {/* Error Message */}
              {submitStatus.type === 'error' && submitStatus.message && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 p-4 rounded-xl text-center bg-red-500/20 text-red-100 border border-red-400/30"
                >
                  {submitStatus.message}
                </motion.div>
              )}
            </motion.form>

            {/* Thank You Popup */}
            <AnimatePresence>
              {showPopup && (
                <motion.div
                  className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  <motion.div
                    className="relative bg-white rounded-3xl shadow-2xl p-10 mx-4 max-w-md w-full text-center overflow-hidden"
                    initial={{ scale: 0.5, opacity: 0, y: 50 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.8, opacity: 0, y: -30 }}
                    transition={{ type: 'spring', damping: 20, stiffness: 300 }}
                  >
                    {/* Animated checkmark circle */}
                    <motion.div
                      className="mx-auto mb-6 w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center shadow-lg"
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: 'spring', damping: 12, stiffness: 200, delay: 0.2 }}
                    >
                      <motion.svg
                        className="w-10 h-10 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </motion.svg>
                    </motion.div>

                    {/* Heading */}
                    <motion.h3
                      className="text-2xl font-bold text-steel mb-2"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.35 }}
                    >
                      Thank You!
                    </motion.h3>

                    {/* Message */}
                    <motion.p
                      className="text-gray-500 mb-4 leading-relaxed"
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.45 }}
                    >
                      Your message has been sent successfully.<br />
                      Our team will get back to you within 24 hours.
                    </motion.p>

                    {/* Brand tag */}
                    <motion.div
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.55 }}
                    >
                      <span className="w-2 h-2 rounded-full bg-primary animate-pulse"></span>
                      <span className="text-sm font-semibold text-primary">Alpha Laser Technologies</span>
                    </motion.div>

                    {/* Progress bar */}
                    <motion.div
                      className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-primary via-blue-500 to-secondary rounded-full"
                      initial={{ width: '100%' }}
                      animate={{ width: '0%' }}
                      transition={{ duration: 3, ease: 'linear' }}
                    />

                    {/* Confetti dots */}
                    {[...Array(8)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="absolute w-2 h-2 rounded-full"
                        style={{
                          backgroundColor: ['rgb(15,14,151)', 'rgb(15,14,151)', '#e32934', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4'][i],
                          top: '40%',
                          left: '50%',
                        }}
                        initial={{ x: 0, y: 0, scale: 0, opacity: 1 }}
                        animate={{
                          x: [0, (Math.cos(i * 45 * Math.PI / 180)) * 120],
                          y: [0, (Math.sin(i * 45 * Math.PI / 180)) * 120],
                          scale: [0, 1.5, 0],
                          opacity: [1, 1, 0],
                        }}
                        transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
                      />
                    ))}
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>

      {/* Map Section */}
      <motion.div 
        className="mt-8 rounded-3xl overflow-hidden shadow-2xl max-w-7xl mx-auto relative h-[500px] border-4 border-primary"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
      >
        {/* Decorative Corner Elements */}
        <div className="absolute top-4 left-4 w-16 h-16 border-l-4 border-t-4 border-primary rounded-tl-2xl z-10 opacity-60 pointer-events-none" />
        <div className="absolute bottom-4 right-4 w-16 h-16 border-r-4 border-b-4 border-primary rounded-br-2xl z-10 opacity-60 pointer-events-none" />
        
        {/* Embedded Google Map */}
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d6605.752355914925!2d76.97662787170464!3d11.046290145895197!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba859b511a3a8c3%3A0x277cfc5c9823302c!2sALPHA%20LASER%20TECHNOLOGIES!5e0!3m2!1sen!2sin!4v1765877315827!5m2!1sen!2sin"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Alpha Laser Technologies Location"
        />
      </motion.div>
      </div>
    </section>
  );
};

export default Contact;
